import dayjs from 'dayjs';
import { validateAnnualWorkingDays, determineRateType } from '../../../utils/rateUtils';

/**
 * RoleCopyService
 * 
 * Service class for handling role copying operations with backwards compatibility.
 * Provides both individual role copying and bulk milestone copying.
 */
export class RoleCopyService {
  constructor() {
    this.copyHistory = new Map();
  }

  /**
   * Validates data structure for backwards compatibility
   */
  validateDataStructure(data) {
    const issues = [];
    const fixes = [];
    
    if (!data || typeof data !== 'object') {
      issues.push('Invalid data object');
      return { isValid: false, issues, fixes };
    }
    
    // Check milestones structure
    if (data.milestones && Array.isArray(data.milestones)) {
      data.milestones.forEach((milestone, mIndex) => {
        if (!milestone.id) {
          issues.push(`Milestone ${mIndex} missing ID`);
          fixes.push(`Will generate safe ID for milestone ${mIndex}`);
        }
        
        if (milestone.roles && Array.isArray(milestone.roles)) {
          milestone.roles.forEach((role, rIndex) => {
            // Check essential role fields
            if (!role.id) {
              issues.push(`Role ${rIndex} in milestone "${milestone.name || mIndex}" missing ID`);
              fixes.push(`Will generate safe ID for role ${rIndex}`);
            }
            
            // Check timeline format compatibility
            if (role.dateRanges && Array.isArray(role.dateRanges)) {
              const hasLegacyFormat = role.dateRanges.some(range => 
                !range.timelineVersion || range.timelineVersion !== '3.0'
              );
              if (hasLegacyFormat) {
                fixes.push(`Will safely migrate timeline format for role "${role.name || role.id}"`);
              }
            } else if (!role.dateRanges) {
              fixes.push(`Will create timeline data for role "${role.name || role.id}"`);
            }
            
            // Check rate structure
            if (typeof role.rate !== 'number') {
              issues.push(`Role "${role.name || role.id}" has invalid rate: ${role.rate}`);
              fixes.push(`Will set safe default rate for role "${role.name || role.id}"`);
            }
            
            // Check allocation
            if (typeof role.allocation !== 'number' || role.allocation < 0 || role.allocation > 100) {
              issues.push(`Role "${role.name || role.id}" has invalid allocation: ${role.allocation}`);
              fixes.push(`Will set safe allocation (100%) for role "${role.name || role.id}"`);
            }
          });
        }
      });
    }
    
    const isValid = issues.length === 0;
    
    if (isValid) {
      console.log('✅ RoleCopyService: All data structures are valid');
    } else {
      console.log(`⚠️ RoleCopyService: Found ${issues.length} issues, ${fixes.length} automatic fixes available`);
    }
    
    return { isValid, issues, fixes };
  }

  /**
   * Creates a safe copy of a role for a target milestone
   */
  createRoleCopy(sourceRole, targetMilestone, index = 0) {
    // BACKWARDS COMPATIBILITY: Preserve all existing fields
    const preservedFields = { ...sourceRole };
    
    // Calculate target milestone timeline
    const targetMilestoneStart = dayjs(targetMilestone.start_date);
    const targetMilestoneEnd = dayjs(targetMilestone.end_date);
    const targetStartCalendarMonth = targetMilestoneStart.month() + 1;
    const targetEndCalendarMonth = targetMilestoneEnd.month() + 1;
    const targetDuration = Math.max(1, targetMilestoneEnd.diff(targetMilestoneStart, 'month') + 1);

    // BACKWARDS COMPATIBILITY: Handle legacy working days
    const legacyWorkingDays = sourceRole.annualWorkingDays || 
                             sourceRole.effectiveAnnualWorkingDays || 
                             (sourceRole.company === 'CA' ? 252 : 260) || 
                             252; // Safe default
    
    const copiedRoleWorkingDays = validateAnnualWorkingDays(
      legacyWorkingDays,
      `copied role ${sourceRole.name || 'Unknown'}`
    );

    // BACKWARDS COMPATIBILITY: Handle legacy timeline formats
    let newDateRanges;
    if (sourceRole.dateRanges && sourceRole.dateRanges.length > 0) {
      // Preserve existing dateRanges structure but update for target milestone
      const firstRange = sourceRole.dateRanges[0];
      const isLegacyFormat = !firstRange.timelineVersion || firstRange.timelineVersion !== '3.0';
      
      console.log(`🔍 Role ${sourceRole.name}: ${isLegacyFormat ? 'Legacy' : 'Modern'} timeline format detected`);
      
      newDateRanges = [{
        startMonth: targetStartCalendarMonth,
        endMonth: targetEndCalendarMonth,
        allocation: firstRange.allocation || sourceRole.allocation || 100,
        timelineVersion: '3.0', // Upgrade to modern format
        // BACKWARDS COMPATIBILITY: Preserve any custom fields from original range
        ...Object.fromEntries(
          Object.entries(firstRange).filter(([key]) => 
            !['startMonth', 'endMonth', 'allocation', 'timelineVersion'].includes(key)
          )
        )
      }];
    } else {
      // BACKWARDS COMPATIBILITY: Create dateRanges for roles that don't have them
      console.log(`🔍 Role ${sourceRole.name}: No dateRanges found, creating from milestone timeline`);
      newDateRanges = [{
        startMonth: targetStartCalendarMonth,
        endMonth: targetEndCalendarMonth,
        allocation: sourceRole.allocation || 100,
        timelineVersion: '3.0'
      }];
    }

    // BACKWARDS COMPATIBILITY: Preserve legacy rate structure
    const legacyRateFields = {
      rate: sourceRole.rate || 0,
      rateAdjustmentInfo: sourceRole.rateAdjustmentInfo || null,
      calculatedForYear: sourceRole.calculatedForYear || null,
      lastRateUpdate: sourceRole.lastRateUpdate || null,
      rateType: sourceRole.rateType || determineRateType(copiedRoleWorkingDays)
    };

    return {
      // BACKWARDS COMPATIBILITY: Start with ALL original fields
      ...preservedFields,
      
      // Override only the fields that MUST change for the new milestone
      id: `${targetMilestone.id}-${sourceRole.name || 'role'}-${Date.now()}-${index}`,
      milestone_id: targetMilestone.id,
      
      // BACKWARDS COMPATIBILITY: Preserve original CAPEX classification unless target overrides
      capex_classification: sourceRole.capex_classification || targetMilestone.capex_classification || 'OPEX',
      capex_source: sourceRole.capex_source || 'milestone',
      
      // Update timeline fields for target milestone
      milestone_start_month: targetStartCalendarMonth,
      milestone_end_month: targetEndCalendarMonth,
      overlap_months: targetDuration,
      
      // BACKWARDS COMPATIBILITY: Preserve timeline preferences
      useDefaultTimeline: sourceRole.useDefaultTimeline !== false, // Default to true if undefined
      dateRanges: newDateRanges,
      defaultAllocation: sourceRole.defaultAllocation || sourceRole.allocation || 100,
      
      // BACKWARDS COMPATIBILITY: Preserve all rate-related fields
      ...legacyRateFields,
      
      // Update timeline version (safe upgrade)
      timelineVersion: '3.0',
      lastTimelineUpdate: new Date().toISOString(),
      
      // BACKWARDS COMPATIBILITY: Preserve working days structure
      annualWorkingDays: copiedRoleWorkingDays,
      effectiveAnnualWorkingDays: sourceRole.effectiveAnnualWorkingDays || copiedRoleWorkingDays
    };
  }

  /**
   * Copies a single role to a target milestone
   */
  copyRole(sourceRole, targetMilestone) {
    console.log(`🔄 RoleCopyService: Copying role "${sourceRole.name}" to milestone "${targetMilestone.name}"`);
    
    const copiedRole = this.createRoleCopy(sourceRole, targetMilestone);
    
    // Track copy operation
    this.copyHistory.set(copiedRole.id, {
      sourceRoleId: sourceRole.id,
      sourceMilestoneName: sourceRole.milestone_id,
      targetMilestoneName: targetMilestone.name,
      copiedAt: new Date().toISOString(),
      type: 'individual'
    });
    
    console.log(`✅ RoleCopyService: Successfully copied role "${sourceRole.name}"`);
    return copiedRole;
  }

  /**
   * Copies all roles from source milestone to target milestone
   */
  copyAllRoles(sourceMilestone, targetMilestone) {
    if (!sourceMilestone.roles || sourceMilestone.roles.length === 0) {
      console.warn('⚠️ RoleCopyService: No roles to copy from source milestone');
      return [];
    }

    console.log(`🔄 RoleCopyService: Copying ${sourceMilestone.roles.length} roles from "${sourceMilestone.name}" to "${targetMilestone.name}"`);
    
    // BACKWARDS COMPATIBILITY: Validate source data
    const sourceValidation = this.validateDataStructure({ milestones: [sourceMilestone] });
    const targetValidation = this.validateDataStructure({ milestones: [targetMilestone] });
    
    if (!sourceValidation.isValid || !targetValidation.isValid) {
      console.warn('⚠️ RoleCopyService: Data integrity issues detected, but proceeding with safe copy...');
    }

    // Log data structure analysis
    sourceMilestone.roles.forEach((role, index) => {
      const hasLegacyTimeline = !role.dateRanges || role.dateRanges.length === 0 || 
        role.dateRanges.some(r => !r.timelineVersion || r.timelineVersion !== '3.0');
      const hasLegacyRate = !role.rateAdjustmentInfo && !role.lastRateUpdate;
      const hasLegacyWorkingDays = !role.annualWorkingDays && !role.effectiveAnnualWorkingDays;
      
      if (hasLegacyTimeline || hasLegacyRate || hasLegacyWorkingDays) {
        console.log(`🔍 Role ${index + 1} (${role.name}): Legacy format detected - Timeline: ${hasLegacyTimeline}, Rate: ${hasLegacyRate}, WorkingDays: ${hasLegacyWorkingDays}`);
      }
    });

    // Create copied roles
    const copiedRoles = sourceMilestone.roles.map((sourceRole, index) => {
      const copiedRole = this.createRoleCopy(sourceRole, targetMilestone, index);
      
      // Add bulk copy metadata
      copiedRole.copiedFrom = {
        milestoneId: sourceMilestone.id,
        milestoneName: sourceMilestone.name,
        originalRoleId: sourceRole.id,
        copiedAt: new Date().toISOString(),
        bulkCopyOperation: true
      };
      
      // Track copy operation
      this.copyHistory.set(copiedRole.id, {
        sourceRoleId: sourceRole.id,
        sourceMilestoneName: sourceMilestone.name,
        targetMilestoneName: targetMilestone.name,
        copiedAt: new Date().toISOString(),
        type: 'bulk',
        bulkOperationId: `bulk-${Date.now()}`
      });
      
      return copiedRole;
    });
    
    console.log('✅ RoleCopyService: All legacy fields preserved, safe upgrades applied');
    console.log(`✅ RoleCopyService: Successfully copied ${copiedRoles.length} roles to "${targetMilestone.name}"`);
    
    return copiedRoles;
  }

  /**
   * Gets copy history for debugging
   */
  getCopyHistory() {
    return Array.from(this.copyHistory.entries()).map(([roleId, history]) => ({
      roleId,
      ...history
    }));
  }

  /**
   * Clears copy history
   */
  clearCopyHistory() {
    this.copyHistory.clear();
  }

  /**
   * Gets statistics about copy operations
   */
  getCopyStats() {
    const history = this.getCopyHistory();
    return {
      totalCopies: history.length,
      individualCopies: history.filter(h => h.type === 'individual').length,
      bulkCopies: history.filter(h => h.type === 'bulk').length,
      uniqueBulkOperations: new Set(history.filter(h => h.bulkOperationId).map(h => h.bulkOperationId)).size
    };
  }
}

// Export singleton instance
export const roleCopyService = new RoleCopyService();